import * as React from "react";

/**
 * The three analytics charts on the mark overview, drawn as plain SVG so the app takes no charting dependency.
 *
 * Colours come from the semantic tokens through Tailwind's `fill-*` / `stroke-*` utilities, never raw palette
 * values, so both themes work without a dark variant on each mark. Every chart only plots numbers the API sent:
 * averages, spreads and buckets summarise stored results and are never a recalculated mark.
 */

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex h-40 items-center justify-center text-sm text-text-weak">{children}</div>
);

// ------------------------------------------------------------------- gauge

export const PassRateGauge: React.FC<{ passRate?: number; passMark?: number; passMarkIsPercent?: boolean }> = ({
    passRate,
    passMark,
    passMarkIsPercent,
}) => {
    if (passMark === undefined || passMark === null) {
        return (
            <Empty>
                <span className="max-w-[16rem] text-center">
                    No pass mark configured. Mark a band as passing on this mark type's grade scale to see a pass rate.
                </span>
            </Empty>
        );
    }
    if (passRate === undefined || passRate === null) return <Empty>No results yet.</Empty>;

    const radius = 80;
    const length = Math.PI * radius;
    const filled = (Math.max(0, Math.min(100, passRate)) / 100) * length;
    const arc = `M 20 100 A ${radius} ${radius} 0 0 1 180 100`;

    return (
        <figure className="flex flex-col items-center">
            <svg viewBox="0 0 200 125" className="w-full max-w-[16rem]" role="img" aria-label={`Pass rate ${passRate.toFixed(1)} percent`}>
                <path d={arc} fill="none" strokeWidth={26} className="stroke-surface-muted" />
                <path
                    d={arc}
                    fill="none"
                    strokeWidth={26}
                    strokeDasharray={`${filled} ${length}`}
                    className={passRate >= 50 ? "stroke-primary" : "stroke-warning"}
                />
                <text x={100} y={95} textAnchor="middle" className="fill-text-strong text-[22px] font-semibold">
                    {passRate.toFixed(1)}%
                </text>
                <text x={20} y={118} textAnchor="middle" className="fill-text-weak text-[10px]">
                    0
                </text>
                <text x={180} y={118} textAnchor="middle" className="fill-text-weak text-[10px]">
                    100
                </text>
            </svg>
            <figcaption className="mt-1 text-xs text-text-weak">
                Pass mark {passMark}
                {passMarkIsPercent ? "%" : ""}
            </figcaption>
        </figure>
    );
};

// ------------------------------------------------------------ distribution

/** Catmull-Rom through the points, as cubic Béziers - a smooth line that still passes through every bucket. */
function smoothPath(points: Array<[number, number]>): string {
    if (points.length < 2) return "";
    let d = `M ${points[0][0]} ${points[0][1]}`;
    for (let i = 0; i < points.length - 1; i++) {
        const [x0, y0] = points[Math.max(0, i - 1)];
        const [x1, y1] = points[i];
        const [x2, y2] = points[i + 1];
        const [x3, y3] = points[Math.min(points.length - 1, i + 2)];
        d += ` C ${x1 + (x2 - x0) / 6} ${y1 + (y2 - y0) / 6}, ${x2 - (x3 - x1) / 6} ${y2 - (y3 - y1) / 6}, ${x2} ${y2}`;
    }
    return d;
}

export const DistributionChart: React.FC<{ buckets: number[]; label: string }> = ({ buckets, label }) => {
    const total = buckets.reduce((a, b) => a + b, 0);
    if (!total) return <Empty>No results yet.</Empty>;

    const width = 320;
    const height = 170;
    const left = 30;
    const bottom = 140;
    const top = 10;
    const max = Math.max(...buckets, 1);
    const x = (i: number) => left + ((i + 0.5) / buckets.length) * (width - left - 10);
    const y = (v: number) => bottom - (v / max) * (bottom - top);
    const points = buckets.map((v, i) => [x(i), y(v)] as [number, number]);
    const ticks = Array.from(new Set([0, Math.round(max / 2), max]));

    return (
        <figure>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={`Distribution of ${label}`}>
                {ticks.map((t) => (
                    <g key={t}>
                        <line x1={left} x2={width - 10} y1={y(t)} y2={y(t)} className="stroke-border" strokeDasharray="3 3" />
                        <text x={left - 6} y={y(t) + 3} textAnchor="end" className="fill-text-weak text-[9px]">
                            {t}
                        </text>
                    </g>
                ))}
                <line x1={left} x2={width - 10} y1={bottom} y2={bottom} className="stroke-border-strong" />
                {buckets.map((_, i) => (
                    <text key={i} x={x(i)} y={bottom + 14} textAnchor="middle" className="fill-text-weak text-[9px]">
                        {i * 10}
                    </text>
                ))}
                <path d={`${smoothPath(points)} L ${x(buckets.length - 1)} ${bottom} L ${x(0)} ${bottom} Z`} className="fill-primary-tint" />
                <path d={smoothPath(points)} fill="none" strokeWidth={2} className="stroke-primary" />
                {points.map(([px, py], i) => (
                    <circle key={i} cx={px} cy={py} r={2.5} className="fill-primary">
                        <title>{`${i * 10}-${i === 9 ? 100 : i * 10 + 9}%: ${buckets[i]} student(s)`}</title>
                    </circle>
                ))}
            </svg>
            <figcaption className="text-center text-xs text-text-weak">
                {label} - {total} result{total === 1 ? "" : "s"}
            </figcaption>
        </figure>
    );
};

// -------------------------------------------------------- division analysis

export interface AnalysisBar {
    name: string;
    average?: number;
    standardDeviation?: number;
}

export const DivisionAnalysisChart: React.FC<{ bars: AnalysisBar[]; overallAverage?: number }> = ({ bars, overallAverage }) => {
    const shown = bars.filter((b) => b.average !== undefined && b.average !== null);
    if (!shown.length) return <Empty>No division results yet.</Empty>;

    const width = Math.max(320, shown.length * 70 + 40);
    const height = 190;
    const left = 30;
    const bottom = 150;
    const top = 10;
    const slot = (width - left - 10) / shown.length;
    const barWidth = Math.min(24, slot / 3);
    const y = (v: number) => bottom - (Math.max(0, Math.min(100, v)) / 100) * (bottom - top);

    return (
        <figure>
            <div className="overflow-x-auto">
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[20rem]" role="img" aria-label="Average and spread per division">
                    {[0, 20, 40, 60, 80, 100].map((t) => (
                        <g key={t}>
                            <line x1={left} x2={width - 10} y1={y(t)} y2={y(t)} className="stroke-border" strokeDasharray="3 3" />
                            <text x={left - 6} y={y(t) + 3} textAnchor="end" className="fill-text-weak text-[9px]">
                                {t}
                            </text>
                        </g>
                    ))}
                    {shown.map((b, i) => {
                        const cx = left + slot * (i + 0.5);
                        return (
                            <g key={b.name}>
                                <rect x={cx - barWidth - 1} y={y(b.average!)} width={barWidth} height={bottom - y(b.average!)} className="fill-primary">
                                    <title>{`${b.name}: average ${b.average!.toFixed(1)}%`}</title>
                                </rect>
                                <rect
                                    x={cx + 1}
                                    y={y(b.standardDeviation ?? 0)}
                                    width={barWidth}
                                    height={bottom - y(b.standardDeviation ?? 0)}
                                    className="fill-accent"
                                >
                                    <title>{`${b.name}: standard deviation ${(b.standardDeviation ?? 0).toFixed(1)}`}</title>
                                </rect>
                                <text x={cx} y={bottom + 14} textAnchor="middle" className="fill-text text-[9px]">
                                    {b.name.length > 14 ? `${b.name.slice(0, 13)}…` : b.name}
                                </text>
                            </g>
                        );
                    })}
                    {overallAverage !== undefined && overallAverage !== null && (
                        <g>
                            <line x1={left} x2={width - 10} y1={y(overallAverage)} y2={y(overallAverage)} className="stroke-text-weak" strokeDasharray="6 3" />
                            <text x={width - 12} y={y(overallAverage) - 4} textAnchor="end" className="fill-text-weak text-[9px]">
                                Average {overallAverage.toFixed(1)}%
                            </text>
                        </g>
                    )}
                    <line x1={left} x2={width - 10} y1={bottom} y2={bottom} className="stroke-border-strong" />
                </svg>
            </div>
            <figcaption className="mt-1 flex justify-center gap-4 text-xs text-text-weak">
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" /> Average %
                </span>
                <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-accent" /> Standard deviation
                </span>
            </figcaption>
        </figure>
    );
};
